import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

// Configuración global: enviar cookies httpOnly en todas las peticiones.
axios.defaults.withCredentials = true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthCtx = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  forgot: async () => {},
  changePassword: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  // user: null (cargando), false (no autenticado), objeto (autenticado)
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignorar
    }
    setUser(false);
  }, []);

  const forgot = useCallback(async (email) => {
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  }, []);

  const changePassword = useCallback(async (current_password, new_password) => {
    await axios.post(`${API}/auth/change-password`, { current_password, new_password });
    await refresh();
  }, [refresh]);

  // Estabilizar el value del provider para que los consumidores no re-rendericen
  // en cada render de AuthProvider aunque su estado no haya cambiado.
  const value = useMemo(
    () => ({ user, loading, login, logout, forgot, changePassword, refresh }),
    [user, loading, login, logout, forgot, changePassword, refresh],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
